import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Toast } from 'primereact/toast';
import BindSuccessPage from './BindSuccessPage';
import DataRoomStep from './DataRoomStep';
import ChatDrawer from './ChatDrawer';
import AppetiteDrawer from './AppetiteDrawer';
import { logout } from '../../services/authApi';

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
/** Return NA for blank/null/undefined/null-string values */
const NOT_AVAILABLE = 'NA';

const val = (v?: string | null) =>
  v && v !== 'null' && v.trim() !== '' ? v : NOT_AVAILABLE;

/** Format currency; NA when there is no figure. */
const fmtCurrency = (v?: string | number) => {
  if (v === '' || v === null || v === undefined) return NOT_AVAILABLE;
  const num = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(num)) return NOT_AVAILABLE;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0,
  }).format(num);
};

/** Parse a premium/fee string to number, defaulting to 0 for empty */
const toNum = (v?: string) => {
  if (!v || v.trim() === '') return 0;
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

/** Strip trailing time part from API date strings like "Jun 12, 2025 12:00 am" */
const fmtDate = (v?: string) => {
  if (!v || v.trim() === '') return NOT_AVAILABLE;
  // Remove " 12:00 am", " 11:59 pm", etc.
  return v.replace(/\s+\d{1,2}:\d{2}\s*(am|pm)/i, '').trim() || NOT_AVAILABLE;
};

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */
const QuoteDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useRef<Toast>(null);
  const routeState = (location.state ?? {}) as { quoteId?: string; status?: string; company?: string };
  // Opened from the Carrier dashboard (/carrier/quote/:id) or the SubCarrier dashboard (/my-quotes/quote/:id): same
  // steps as the agency page, but the quote is loaded from quote-context and `id` is the NowCerts insuredDatabaseId.
  // The page keeps its layout and shows an alert when loading fails.
  const isCarrierView = location.pathname.startsWith('/carrier/');
  const isSubCarrierView = location.pathname.startsWith('/my-quotes/');
  const keepLayout = isCarrierView || isSubCarrierView;
  const backPath = isCarrierView ? '/carrier' : isSubCarrierView ? '/my-quotes' : '/quote-history';
  const backLabel = isCarrierView || isSubCarrierView ? 'Back to Dashboard' : 'Back to Quote History';

  const [detail, setDetail] = useState<any>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(true);
  // Why the detail could not be loaded; the carrier / SubCarrier views still render its layout (with NA values) and shows this.
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasFetchedDetail = React.useRef(false);

  useEffect(() => {
    if (!id || hasFetchedDetail.current) return;
    hasFetchedDetail.current = true;

    const token = localStorage.getItem('igtoken');
    const aiApiUrl = process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url;

    if (keepLayout) {
      // id is the insuredDatabaseId from the quotes list; the page is built from quote-context,
      // which takes it as both the quoteId and the insuredId.
      if (!aiApiUrl || !token) {
        setLoadError('Could not load quote details. Please sign in again.');
        setIsLoadingDetail(false);
        return;
      }
      axios
        .post(
          `${aiApiUrl}/ai/quote-context`,
          { quoteId: id, insuredId: id },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              'X-NC-Token': localStorage.getItem('NCToken') || '',
            },
          },
        )
        .then((res: any) => {
          if (!res?.data?.context) throw new Error('quote-context returned no context');
          setDetail(res.data.context);
        })
        .catch((err: any) => {
          const status = err?.response?.status;
          if (status === 401) { logout(); navigate('/login'); return; }
          console.warn('[quote-context] load failed:', status, err?.response?.data ?? err?.message);
          setLoadError(
            status === 404 ? 'Quote not found.'
            : status === 403 ? 'You do not have access to this quote.'
            : `Could not load quote details${status ? ` (HTTP ${status})` : ''}. Please try again.`);
        })
        .finally(() => setIsLoadingDetail(false));
      return;
    }
    // Prospect submissions carry their id on the URL; there is no quote row.
    const insuredId = new URLSearchParams(location.search).get('insuredId') || '';
    // A prospect has no NCMtmSubmission row, so this would only ever 404 - its
    // detail comes from the quote-context call below.
    if (!insuredId) axios({
      method: 'get',
      url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/GetQuoteByTransactionId`,
      params: { transactionId: id },
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res: any) => {
        const raw = res.data;
        const parsed = typeof raw?.requestData === 'string'
          ? JSON.parse(raw.requestData)
          : raw;
        setDetail(parsed);
      })
      .catch((error: any) => {
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate('/login');
        } else {
          setDetail(null);
          toast.current?.show({
            severity: 'error', summary: 'Error',
            detail: error.response?.data?.message
              ? `Error ${error.response.status}: ${error.response.data.message}`
              : 'Failed to load quote details. Please try again.',
            life: 3000,
          });
        }
      })
      .finally(() => setIsLoadingDetail(false));

    // 2. Pre-load quote data into the AI carrier graph (fire-and-forget).
    //    Runs in parallel — never blocks the UI or quote detail fetch.
    if (aiApiUrl && token) {
      axios
        .post(
          `${aiApiUrl}/ai/quote-context`,
          insuredId ? { quoteId: id, insuredId } : { quoteId: id },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              // A prospect read needs the agent's NowCerts session.
              'X-NC-Token': localStorage.getItem('NCToken') || '',
            },
          },
        )
        .then((res: any) => {
          // A prospect has no NCMtmSubmission row - this is all there is.
          if (insuredId && res?.data?.context) setDetail(res.data.context);
        })
        .catch((err: any) => {
          console.warn('[AI] quote-context call failed:', err?.message);
        })
        .finally(() => { if (insuredId) setIsLoadingDetail(false); });
    } else if (insuredId) {
      setIsLoadingDetail(false);   // nothing to wait for
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate, location.search, keepLayout]);
  /* Chat drawer — chatQuoteId is set to the AI-resolved quoteId once
     a document is uploaded. Falls back to the transaction ID. */
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isAppetiteOpen, setIsAppetiteOpen] = useState(false);
  const [chatQuoteId, setChatQuoteId] = useState<string>('');

  /* Steps */
  const [activeStep, setActiveStep] = useState<1 | 2 | 3>(1);
  const [submissionData, setSubmissionData] = useState<any>(null);
  const [submissionLoading, setSubmissionLoading] = useState(false);

  /* Collapsible sections — secondary sections start collapsed to keep page compact */
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set(['owner', 'premiums', 'vehicles', 'drivers']));
  const toggleSection = (key: string) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Key for the Data Room (documents, submission, AI graph). In the carrier / SubCarrier views `id` is a NowCerts
  // insuredDatabaseId, so the quote number is used instead, falling back to the insuredDatabaseId.
  const quoteKey = keepLayout ? (routeState.quoteId || id) : id;

  const handleContinueToDataRoom = async () => {
    const qid = quoteKey;
    if (qid) {
      try {
        setSubmissionLoading(true);
        const token = localStorage.getItem('igtoken');
        const res = await axios.get(
          `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/getsubmission`,
          { params: { quoteId: qid }, headers: { Authorization: `Bearer ${token}` } }
        );
        setSubmissionData(res.data ?? null);
      } catch (error: any) {
        if (error?.response?.status === 401) { localStorage.clear(); navigate('/login'); return; }
        setSubmissionData(null);
        toast.current?.show({
          severity: 'error', summary: 'Error',
          detail: error.response?.data?.message
            ? `Error ${error.response.status}: ${error.response.data.message}`
            : 'Failed to load submission data.',
          life: 3000,
        });
      } finally {
        setSubmissionLoading(false);
      }
    }
    setActiveStep(2);
  };
  /* Early returns — after all hooks */
  if (isLoadingDetail) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: '#555' }}>
        Loading quote details…
      </div>
    );
  }

  if (!detail && !keepLayout) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '60vh', justifyContent: 'center', gap: 12 }}>
        <p style={{ color: '#dc2626' }}>Failed to load quote details.</p>
        <button className="qdp-back" onClick={() => navigate(backPath)}>← {backLabel}</button>
      </div>
    );
  }

  // Handle APIs that return PascalCase or flat structures
  // The carrier / SubCarrier views keep their layout when there is no detail: empty sections, NA values.
  const raw = (detail ?? {}) as any;
  const si = raw.submissionInfo ?? raw.SubmissionInfo ?? (keepLayout ? {} : undefined);
  const ii = raw.insuredInfo ?? raw.InsuredInfo ?? (keepLayout ? {} : undefined);

  if (!si || !ii) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '60vh', justifyContent: 'center', gap: 12 }}>
        <p style={{ color: '#dc2626' }}>Quote detail structure is not available for this submission.</p>
        <button className="qdp-back" onClick={() => navigate(backPath)}>← {backLabel}</button>
      </div>
    );
  }

  /* Safe sub-objects */
  const siVehicles: any[] = (si.vehicles ?? []).filter(Boolean);
  const siDrivers: any[] = (si.drivers ?? []).filter(Boolean);

  /* Decline rule — vehicle types not eligible for MTM coverage.
  const DECLINED_VEHICLE_KEYWORDS = ['light', 'box truck'];
  const declinedVehicles = siVehicles.filter(v =>
    DECLINED_VEHICLE_KEYWORDS.some(kw => (v.type ?? '').toLowerCase().includes(kw))
  );
  */
  const iiOwner = ii.owner ?? { name: '', email: '', phone: '' };

  /* Real premiums — prefer PolicyObject values over submissionInfo placeholders */
  const policyObj = raw.original_json?.PolicyObject ?? {};
  const premiumRows: { label: string; value: number }[] = [
    ...(policyObj.ALPremium  ? [{ label: 'Auto Liability Premium',    value: policyObj.ALPremium  }] : []),
    ...(policyObj.GLPremium  ? [{ label: 'General Liability Premium', value: policyObj.GLPremium  }] : []),
    ...(policyObj.MedPremium ? [{ label: 'Medical Premium',           value: policyObj.MedPremium }] : []),
    ...(policyObj.UMPremium  ? [{ label: 'UM / UIM Premium',          value: policyObj.UMPremium  }] : []),
    ...(policyObj.PIPCoverage !== '0' && policyObj.PIPModifications
        ? [{ label: 'PIP Premium', value: policyObj.PIPModifications }] : []),
  ];

  const feeRows: { label: string; value: number }[] = [
    ...(policyObj.PolicyFee      ? [{ label: 'Policy Fee',   value: policyObj.PolicyFee      }] : []),
    ...(policyObj.StateTax       ? [{ label: 'State Tax',    value: policyObj.StateTax       }] : []),
    ...(policyObj.DomicileTax    ? [{ label: 'Domicile Tax', value: policyObj.DomicileTax    }] : []),
    ...(policyObj.Capitalization ? [{ label: 'Capital Fee',  value: policyObj.Capitalization }] : []),
  ];

  const totalPremium = premiumRows.reduce((a, r) => a + r.value, 0);
  const totalFees    = feeRows.reduce((a, r) => a + r.value, 0);

  /* Misc */
  const ownerInitials = (iiOwner.name || '').split(' ').slice(0, 2).map((w: string) => w[0]).join('').toUpperCase();

  const statusClass = (s?: string) =>
    ({ Approved: 'badge badge--hero badge--hero-approved', Pending: 'badge badge--hero badge--hero-pending', Rejected: 'badge badge--hero badge--hero-rejected' }[s ?? ''] ?? 'badge badge--hero badge--hero-pending');

  const Chip = ({ label, v }: { label: string; v?: string | null }) => {
    const yes = (v ?? '').toLowerCase() === 'yes';
    return (
      <span className={`coverage-chip ${yes ? 'coverage-chip--yes' : 'coverage-chip--no'}`}>
        {yes
          ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        }
        {label}
      </span>
    );
  };

  // In the carrier / SubCarrier views `id` is an insuredDatabaseId, so the quote number comes from the route state.
  const displayQuoteNumber = routeState.quoteId;
  const displayStatus = routeState.status;
  const aiQuoteId = keepLayout ? (displayQuoteNumber ?? '') : (id || routeState.quoteId || '');

  const pageVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -8 },
  };

  /* ─── render ─── */
  return (
    <div className="qdp">
      <Toast ref={toast} position="top-right" />

      {/* ── Back button ── */}
      <button className="qdp-back" onClick={() => activeStep === 1 ? navigate(backPath) : setActiveStep((activeStep - 1) as 1 | 2 | 3)}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
        </svg>
        {activeStep === 1 ? backLabel : activeStep === 2 ? 'Back to Quote Details' : 'Back to Data Room'}
      </button>

      {/* ── Step indicator + Ask AI button ── */}
      <div className="qdp-step-nav">
        <button
          className={`qdp-step-nav__item ${activeStep === 1 ? 'qdp-step-nav__item--active' : 'qdp-step-nav__item--done'}`}
          onClick={() => setActiveStep(1)}
        >
          <span className="qdp-step-nav__num">
            {activeStep > 1
              ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              : '1'
            }
          </span>
          Quote Details
        </button>

        <div className="qdp-step-nav__connector" />

        <button
          className={`qdp-step-nav__item ${activeStep === 2 ? 'qdp-step-nav__item--active' : activeStep > 2 ? 'qdp-step-nav__item--done' : ''}`}
          onClick={handleContinueToDataRoom}
        >
          <span className="qdp-step-nav__num">
            {activeStep > 2
              ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              : '2'
            }
          </span>
          Data Room
        </button>

        <div className="qdp-step-nav__connector" />

        <button
          className={`qdp-step-nav__item ${activeStep === 3 ? 'qdp-step-nav__item--active' : ''}`}
          disabled={activeStep < 3}
        >
          <span className="qdp-step-nav__num">3</span>
          Complete
        </button>

        <div className="qdp-step-nav__divider" />

        <button className="qdp-step-nav__ask-ai" onClick={() => setIsChatOpen(true)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Ask AI
        </button>

        <button className="qdp-step-nav__ask-ai" onClick={() => setIsAppetiteOpen(true)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
          </svg>
          Carrier Appetite Guide
        </button>
      </div>

      {/* ══════════════════════════════════
          STEP 1 — Quote Details
      ══════════════════════════════════ */}
      <AnimatePresence mode="wait">
        {activeStep === 1 && (
          <motion.div key="step1" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.2 }} style={{ paddingBottom: 100 }}>

            {/* Page Header */}
            <div className="dr-header" style={{ marginBottom: 12 }}>
              <div className="dr-header__text">
                <div className="dr-header__company-row">
                  <h2>{val(ii.name)}</h2>
                  <span className={statusClass(displayStatus)}>{displayStatus ?? 'Pending'}</span>
                </div>
                <div className="dr-header__meta">
                  <span>{displayQuoteNumber ?? 'QT---'}</span>
                  <span className="dr-header__dot" />
                  <span>DOT #{val(ii.dotNumber)}</span>
                </div>
              </div>
            </div>

            {loadError && (
              <div className="qdp-decline-alert" style={{ marginBottom: 12 }}>
                <svg className="qdp-decline-alert__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <div className="qdp-decline-alert__body">
                  <strong>Quote details are unavailable</strong>
                  <p>{loadError}</p>
                </div>
              </div>
            )}

            {/* Quick Stats Bar */}
            <div className="dr-stats-bar" style={{ marginBottom: 12 }}>
              <span className="dr-stats-bar__item">
                <span className="dr-stats-bar__label">Due Premium</span>
                <span className="dr-stats-bar__value">
                  {policyObj.DuePremium ? fmtCurrency(policyObj.DuePremium) : totalPremium + totalFees > 0 ? fmtCurrency(totalPremium + totalFees) : NOT_AVAILABLE}
                </span>
              </span>
              <span className="dr-stats-bar__sep" />
              <span className="dr-stats-bar__item">
                <span className="dr-stats-bar__label">Effective Date</span>
                <span className="dr-stats-bar__value">{fmtDate(si.effectiveDate)}</span>
              </span>
              <span className="dr-stats-bar__sep" />
              <span className="dr-stats-bar__item">
                <span className="dr-stats-bar__label">Expiration</span>
                <span className="dr-stats-bar__value">{fmtDate(si.expirationDate)}</span>
              </span>
              <span className="dr-stats-bar__sep" />
              <span className="dr-stats-bar__item">
                <span className="dr-stats-bar__label">Experience</span>
                <span className="dr-stats-bar__value">
                  {val(ii.experienceYears)}{ii.experienceYears ? ' yrs' : ''}
                </span>
              </span>
            </div>

            {/* Decline alert — ineligible vehicle types.
                        {declinedVehicles.length > 0 && (
              <div className="qdp-decline-alert" style={{ marginBottom: 12 }}>
                <svg className="qdp-decline-alert__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <div className="qdp-decline-alert__body">
                  <strong>Quote Not Eligible — Ineligible Vehicle Type</strong>
                  <p>
                    This submission includes {declinedVehicles.length} vehicle{declinedVehicles.length > 1 ? 's' : ''} classified as a declined
                    type ({Array.from(new Set(declinedVehicles.map((v: any) => v.type))).join(', ')}). Please redirect this quote to an alternative carrier.
                  </p>
                </div>
              </div>
            )}

            */}

            {/* Two-column */}
            <div className="qdp-columns" style={{ marginBottom: 12 }}>
              {/* Left */}
              <div className="qdp-col">
                {/* Insured Info */}
                <div className={`section-card${collapsed.has('insured') ? ' section-card--collapsed' : ''}`}>
                  <div className="section-card__title" onClick={() => toggleSection('insured')}>
                    <span className="section-card__title-bar" />
                    <svg className="section-card__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                    </svg>
                    Insured Information
                    <svg className="section-card__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                  </div>
                  <div className="section-card__body">
                    <div className="qdp-grid-2">
                      {[
                        { label: 'Legal Entity', v: ii.legalEntity },
                        { label: 'DOT #', v: ii.dotNumber },
                        { label: 'MC #', v: ii.mcNumber },
                        { label: 'Insured ID', v: ii.insuredId },
                        { label: 'Year Established', v: ii.yearEstablished },
                        { label: 'State', v: ii.state },
                      ].map(({ label, v: rawV }) => (
                        <div className="detail-field" key={label}>
                          <span className="detail-field__label">{label}</span>
                          <span className={`detail-field__value${val(rawV) === NOT_AVAILABLE ? ' detail-field__value--muted' : ''}`}>
                            {val(rawV)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="qdp-divider" />
                    <div className="qdp-address-row">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                      </svg>
                      <span>
                        {[ii.address, ii.city, ii.state, ii.zipCode].filter(x => x && x !== 'null').join(', ') || NOT_AVAILABLE}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Owner */}
                <div className={`section-card${collapsed.has('owner') ? ' section-card--collapsed' : ''}`}>
                  <div className="section-card__title" onClick={() => toggleSection('owner')}>
                    <span className="section-card__title-bar" />
                    <svg className="section-card__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                    </svg>
                    Owner / Contact
                    <svg className="section-card__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                  </div>
                  <div className="section-card__body">
                    <div className="qdp-contact-row">
                      <div className="qdp-contact-row__avatar">{ownerInitials || '?'}</div>
                      <div className="qdp-contact-row__info">
                        <div className="qdp-contact-row__name">{val(iiOwner.name)}</div>
                        <div className="qdp-contact-row__details">
                          <span className="qdp-contact-row__item">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
                            </svg>
                            {val(iiOwner.email)}
                          </span>
                          <span className="qdp-contact-row__item">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.77 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 17v-.08z" />
                            </svg>
                            {val(iiOwner.phone)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right */}
              <div className="qdp-col">
                {/* Policy Period */}
                <div className={`section-card${collapsed.has('policy') ? ' section-card--collapsed' : ''}`}>
                  <div className="section-card__title" onClick={() => toggleSection('policy')}>
                    <span className="section-card__title-bar" />
                    <svg className="section-card__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    Policy Period
                    <svg className="section-card__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                  </div>
                  <div className="section-card__body">
                    <div className="qdp-period">
                      <div className="qdp-period__date">
                        <span className="qdp-period__label">Effective</span>
                        <span className="qdp-period__value">{fmtDate(si.effectiveDate)}</span>
                      </div>
                      <svg className="qdp-period__arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                      </svg>
                      <div className="qdp-period__date">
                        <span className="qdp-period__label">Expiration</span>
                        <span className="qdp-period__value">{fmtDate(si.expirationDate)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Coverages */}
                <div className={`section-card${collapsed.has('coverage') ? ' section-card--collapsed' : ''}`}>
                  <div className="section-card__title" onClick={() => toggleSection('coverage')}>
                    <span className="section-card__title-bar" />
                    <svg className="section-card__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    Coverages
                    <svg className="section-card__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                  </div>
                  <div className="section-card__body">
                    <div className="coverage-chips">
                      <Chip label="General Liability" v={si.generalLiability} />
                      <Chip label="Auto Med Payments" v={si.autoMedicalPayments} />
                      <Chip label="PIP" v={si.personalInjuryProtection} />
                      <Chip label="UIIA" v={si.uiia} />
                      <Chip label="UM / UIM" v={si.unUnderinsuredMotorist} />
                      <Chip label="Cargo" v={si.needCargo} />
                      <Chip label="Reefer Breakdown" v={si.reeferBreakdown} />
                      <Chip label="Terminal" v={si.terminal} />
                    </div>
                    <div className="qdp-divider" />
                    <div className="qdp-grid-2">
                      <div className="detail-field">
                        <span className="detail-field__label">Liability Limit</span>
                        <span className={`detail-field__value${!si.liabilityLimit ? ' detail-field__value--muted' : ''}`}>
                          {val(si.liabilityLimit)}
                        </span>
                      </div>
                      <div className="detail-field">
                        <span className="detail-field__label">UIIA Endorsement</span>
                        <span className={`detail-field__value${!si.uiiEndorsement ? ' detail-field__value--muted' : ''}`}>
                          {val(si.uiiEndorsement)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Premiums & Fees */}
            <div className={`section-card${collapsed.has('premiums') ? ' section-card--collapsed' : ''}`} style={{ marginBottom: 12 }}>
              <div className="section-card__title" onClick={() => toggleSection('premiums')}>
                <span className="section-card__title-bar" />
                <svg className="section-card__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
                Premiums &amp; Fees
                <svg className="section-card__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
              </div>
              <div className="section-card__body">
                {/* Policy-level summary tiles */}
                {(policyObj.GrossPremium || policyObj.MinimumPremium || policyObj.TotalPremium || policyObj.DuePremium) && (
                  <div className="qdp-pf-summary">
                    {([
                      { label: 'Gross Premium',   value: policyObj.GrossPremium   },
                      { label: 'Minimum Premium', value: policyObj.MinimumPremium },
                      { label: 'Total Premium',   value: policyObj.TotalPremium   },
                      { label: 'Due Premium',     value: policyObj.DuePremium     },
                    ] as { label: string; value: number }[]).filter(r => r.value).map(({ label, value }) => (
                      <div key={label} className="qdp-pf-summary__item">
                        <span className="qdp-pf-summary__label">{label}</span>
                        <span className="qdp-pf-summary__value">{fmtCurrency(value)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="qdp-pf-grid">
                  {/* Premiums Panel */}
                  <div className="qdp-pf-panel qdp-pf-panel--premium">
                    <div className="qdp-pf-panel__header">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
                      </svg>
                      <span>Premiums</span>
                    </div>
                    <div className="qdp-pf-rows">
                      {premiumRows.map(({ label, value }) => (
                        <div key={label} className="qdp-pf-row">
                          <span className="qdp-pf-row__dot" />
                          <span className="qdp-pf-row__label">{label}</span>
                          <span className="qdp-pf-row__value">{fmtCurrency(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Fees Panel */}
                  <div className="qdp-pf-panel qdp-pf-panel--fees">
                    <div className="qdp-pf-panel__header">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      <span>Fees &amp; Taxes</span>
                    </div>
                    <div className="qdp-pf-rows">
                      {feeRows.map(({ label, value }) => (
                        <div key={label} className="qdp-pf-row qdp-pf-row--fees">
                          <span className="qdp-pf-row__dot qdp-pf-row__dot--fees" />
                          <span className="qdp-pf-row__label">{label}</span>
                          <span className="qdp-pf-row__value">{fmtCurrency(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Vehicles */}
            {(
              <div className={`section-card${collapsed.has('vehicles') ? ' section-card--collapsed' : ''}`} style={{ marginBottom: 12 }}>
                <div className="section-card__title" onClick={() => toggleSection('vehicles')}>
                  <span className="section-card__title-bar" />
                  <svg className="section-card__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="3" width="15" height="13" rx="2" />
                    <path d="M16 8h4l3 5v3h-7V8z" />
                    <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
                  </svg>
                  Vehicles ({siVehicles.length})
                  <svg className="section-card__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                </div>
                <div className="section-card__body">
                  {siVehicles.length === 0 && (
                    <div className="qdp-empty-row">No vehicles on this submission.</div>
                  )}
                  <div className="qdp-veh-grid">
                    {siVehicles.map((v, i) => (
                      <div key={i} className="qdp-veh-card">
                        {/* Header */}
                        <div className="qdp-veh-card__header">
                          <div className="qdp-veh-card__icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="1" y="3" width="15" height="13" rx="2" />
                              <path d="M16 8h4l3 5v3h-7V8z" />
                              <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
                            </svg>
                          </div>
                          <div className="qdp-veh-card__heading">
                            <div className="qdp-veh-card__make">{val(v.make)}</div>
                            <div className="qdp-veh-card__type">{val(v.type)}</div>
                          </div>
                          {v.year && v.year !== '' && (
                            <span className="qdp-veh-card__year">{v.year}</span>
                          )}
                        </div>
                        {/* VIN */}
                        <div className="qdp-veh-card__vin">
                          <span className="qdp-veh-card__vin-lbl">VIN</span>
                          <span className="qdp-veh-card__vin-val">{val(v.vin)}</span>
                        </div>
                        {/* Specs grid */}
                        <div className="qdp-veh-card__specs">
                          <div className="qdp-veh-spec">
                            <span className="qdp-veh-spec__lbl">Value</span>
                            <span className="qdp-veh-spec__val">{v.value === '0' ? '$0' : fmtCurrency(v.value)}</span>
                          </div>
                          <div className="qdp-veh-spec">
                            <span className="qdp-veh-spec__lbl">Deductible</span>
                            <span className="qdp-veh-spec__val">{v.deductible === '0' ? '$0' : fmtCurrency(v.deductible)}</span>
                          </div>
                          <div className="qdp-veh-spec">
                            <span className="qdp-veh-spec__lbl">Op. Radius</span>
                            <span className="qdp-veh-spec__val">{val(v.operatingRadius)}</span>
                          </div>
                          <div className="qdp-veh-spec">
                            <span className="qdp-veh-spec__lbl">Team Driver</span>
                            <span className="qdp-veh-spec__val">{val(v.teamDriver)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Drivers */}
            {(
              <div className={`section-card${collapsed.has('drivers') ? ' section-card--collapsed' : ''}`} style={{ marginBottom: 12 }}>
                <div className="section-card__title" onClick={() => toggleSection('drivers')}>
                  <span className="section-card__title-bar" />
                  <svg className="section-card__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  Drivers ({siDrivers.length})
                  <svg className="section-card__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                </div>
                <div className="section-card__body">
                  {siDrivers.length === 0 && (
                    <div className="qdp-empty-row">No drivers on this submission.</div>
                  )}
                  <div className="qdp-drv-grid">
                    {siDrivers.map((d, i) => (
                      <div key={i} className="qdp-drv-card">
                        {/* Header */}
                        <div className="qdp-drv-card__header">
                          <div className="qdp-drv-card__avatar">
                            {val(d.name).charAt(0).toUpperCase()}
                          </div>
                          <div className="qdp-drv-card__heading">
                            <div className="qdp-drv-card__name">{val(d.name)}</div>
                            <div className="qdp-drv-card__sub">
                              DL: {val(d.licenseNumber)} &nbsp;·&nbsp; {val(d.state)}
                            </div>
                          </div>
                          {d.validLicense && (
                            <span className={`qdp-drv-card__badge ${d.validLicense === 'Yes' ? 'qdp-drv-card__badge--ok' : 'qdp-drv-card__badge--no'}`}>
                              {d.validLicense === 'Yes' ? '✓ Valid DL' : '✗ Invalid DL'}
                            </span>
                          )}
                        </div>
                        {/* Specs grid */}
                        <div className="qdp-veh-card__specs">
                          <div className="qdp-veh-spec">
                            <span className="qdp-veh-spec__lbl">Age</span>
                            <span className="qdp-veh-spec__val">{val(d.age)}</span>
                          </div>
                          <div className="qdp-veh-spec">
                            <span className="qdp-veh-spec__lbl">Date of Birth</span>
                            <span className="qdp-veh-spec__val">{fmtDate(d.dateOfBirth)}</span>
                          </div>
                          <div className="qdp-veh-spec">
                            <span className="qdp-veh-spec__lbl">DL Class</span>
                            <span className="qdp-veh-spec__val">{val(d.licenseClass)}</span>
                          </div>
                          <div className="qdp-veh-spec">
                            <span className="qdp-veh-spec__lbl">Issue Date</span>
                            <span className="qdp-veh-spec__val">{fmtDate(d.licenseIssueDate)}</span>
                          </div>
                          <div className="qdp-veh-spec">
                            <span className="qdp-veh-spec__lbl">Violations</span>
                            <span className="qdp-veh-spec__val">{val(d.violations)}</span>
                          </div>
                          <div className="qdp-veh-spec">
                            <span className="qdp-veh-spec__lbl">Owner Op</span>
                            <span className="qdp-veh-spec__val">{val(d.owner)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}



            {/* Action bar */}
            <div className="qdp-action-bar">
              <button className="qdp-action-bar__back" onClick={() => navigate(backPath)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
                </svg>
                {keepLayout ? 'Back to Dashboard' : 'Back to History'}
              </button>
              <button
                className="qdp-action-bar__continue"
                onClick={handleContinueToDataRoom}
                disabled={submissionLoading}
              >
                {submissionLoading ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Loading…
                  </>
                ) : (
                  <>
                    Continue to Data Room
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </button>
            </div>

          </motion.div>
        )}

        {/* ══════════════════════════════════
            STEP 2 — Data Room
        ══════════════════════════════════ */}
        {activeStep === 2 && (
          <motion.div key="step2" className="dr" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.2 }} style={{ paddingBottom: 100 }}>
            <DataRoomStep
              transactionId={quoteKey!}
              siDrivers={siDrivers}
              ii={ii}
              si={si}
              totalPremium={totalPremium}
              totalFees={totalFees}
              quoteId={keepLayout ? quoteKey : (id || routeState.quoteId)}
              status={routeState.status}
              initialData={submissionData}
              onBack={() => setActiveStep(1)}
              onComplete={() => setActiveStep(3)}
              onQuoteIdResolved={(qid) => setChatQuoteId(qid)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════════════════════════════
          STEP 3 — Submission Complete
      ══════════════════════════════════ */}
      {activeStep === 3 && (
        <BindSuccessPage
          company={routeState.company ?? ii.name}
          premium={fmtCurrency(totalPremium + totalFees)}
          effectiveDate={fmtDate(si.effectiveDate)}
          expirationDate={fmtDate(si.expirationDate)}
          onBack={() => setActiveStep(2)}
        />
      )}

      {/* ── AI Chat Drawer ── */}
      <ChatDrawer
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        quoteId={chatQuoteId || aiQuoteId}
        companyName={ii?.name || routeState.company}
      />

      {/* ── Carrier Appetite Guide Drawer ── */}
      <AppetiteDrawer
        isOpen={isAppetiteOpen}
        onClose={() => setIsAppetiteOpen(false)}
        quoteId={chatQuoteId || aiQuoteId}
        companyName={ii?.name || routeState.company}
      />

    </div>
  );
};

export default QuoteDetailPage;
